/**
 * Sorting and note cleaning for the models list.
 *
 * Pure and separate from the table, because both are things that look right
 * while being wrong: a comparator that silently falls back to insertion order,
 * and a note that renders machine metadata as if a person had written it.
 */
import ModelItem from '../../../models/ModelItem';
import { typeLabel } from './modelFamilies';

export type SortKey = 'name' | 'modelId' | 'type' | 'updated' | 'deployed';
export type SortDir = 'asc' | 'desc';

export interface Sort {
  key: SortKey;
  dir: SortDir;
}

/**
 * The value a row sorts on.
 *
 * Dates are the app's `YYYY-MM-DD HH:mm:ss` UTC strings, so they order
 * correctly as text and are compared as text rather than parsed. Missing
 * values become the empty string, which sorts first ascending and last
 * descending, and that is the right way round: a model with no deploy date
 * belongs at the far end of "most recently deployed", not silently among the
 * dated ones.
 */
function valueOf(model: ModelItem, key: SortKey): string | number {
  switch (key) {
    case 'modelId':
      return model.modelId ?? 0;
    case 'type':
      return typeLabel(model.type).toLowerCase();
    case 'updated':
      return model.updatedAt ?? '';
    case 'deployed':
      return model.lastDeployedAt ?? '';
    case 'name':
    default:
      return (model.name ?? '').toLowerCase();
  }
}

/**
 * A sorted copy. Never sorts in place: the array handed in is the store's
 * observable list, and reordering it would reorder it for every other reader.
 */
export function sortModels(models: ModelItem[], sort: Sort): ModelItem[] {
  const factor = sort.dir === 'asc' ? 1 : -1;
  return [...models].sort((a, b) => {
    const left = valueOf(a, sort.key);
    const right = valueOf(b, sort.key);
    if (left === right) {
      // Ties broken by id so the order is stable between renders. Without it
      // two models updated the same day swap places on every keystroke in the
      // search box, which reads as the table flickering.
      return ((a.modelId ?? 0) - (b.modelId ?? 0)) * factor;
    }
    if (typeof left === 'number' && typeof right === 'number') {
      return (left - right) * factor;
    }
    return String(left).localeCompare(String(right)) * factor;
  });
}

/** Clicking the current column flips it; clicking another starts it ascending. */
export function nextSort(current: Sort, key: SortKey): Sort {
  if (current.key !== key) {
    // Dates start newest first, because "most recently updated" is what
    // someone means by sorting on a date column; names and ids start A to Z.
    return { key, dir: key === 'updated' || key === 'deployed' ? 'desc' : 'asc' };
  }
  return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
}

/** The icon for a header, so an unsorted column still says it can be sorted. */
export function sortIcon(sort: Sort, key: SortKey): string {
  if (sort.key !== key) return 'far fa-sort';
  return sort.dir === 'asc' ? 'far fa-sort-up' : 'far fa-sort-down';
}

/**
 * A model note as a person wrote it.
 *
 * The notes field is also where tooling leaves markers: rgip stamps
 * `<!-- rgip:model-frame 2026-08-21 -->` at the top of the notes it maintains.
 * Rendered raw that is the first thing a reader sees in the tooltip, and it
 * reads as the note being corrupted rather than as metadata that was never
 * meant for them.
 *
 * Only HTML comments are stripped. Everything else is left exactly as typed,
 * because guessing at what else is "not really the note" is how a tool starts
 * eating content someone meant to keep.
 */
export function cleanNote(notes?: string): string {
  if (!notes) return '';
  return notes.replace(/<!--[\s\S]*?-->/g, '').trim();
}
